// VERA-885: Internal Users page — design-parity sign-off completed 2026-04-30.
// See PR description for surface verification matrix and any documented divergences.
// VRG-92: Added Signups waitlist tab + grant/reject flow.
import React, { useCallback, useMemo, useState } from 'react'

import { Avatar, Button, Tabs, TABS_CONTENT_GAP } from '@repo/ui'
import { Box, Stack, Typography } from '@mui/material'
import { UsersIcon, UserPlusIcon, PencilIcon, ArchiveIcon, MailIcon, BuildingIcon, Trash2Icon, RotateCcwIcon, LogOutIcon } from 'lucide-react'

import { Header, Table, EmptyState, StatusChip, InviteInternalUserModal, EditInternalUserModal, DeleteInternalUserModal, ArchiveInternalUserModal, AssignCompaniesModal, ActionDropdown, CompanyGroupCell, PermissionsMatrix, BulkUserActionModal } from 'components'
import type { BulkUserAction } from 'components'
import { useDeleteInternalUser } from 'hooks/api/useDeleteInternalUser'
import { useUpdateInternalUser } from 'hooks/api/useUpdateInternalUser'
import { INTERNAL_USER_STATUSES } from 'constants/statuses'
import { getMemberRoleDisplayName } from 'constants/roles'
import { date, getErrorMessage, getNameOrEmailSlug } from 'utils'
import { useDisclosure } from 'hooks'
import { useInternalUsers, type InternalUser } from 'hooks/api/useInternalUsers'
import { useInviteInternalUser } from 'hooks/api/useInviteInternalUser'
import { useSnackbar } from 'contexts'
import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal'
import { useRevokeUserSessions } from 'hooks/api/useUserSessions'
import { useUserProfileQuery } from 'hooks/api/useUserProfileQuery'
import type { Company } from 'types'

const DEFAULT_PAGINATION = { page: 0, pageSize: 25 }
const MAX_VISIBLE_COMPANIES = 3
const cellFlexSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

// ── Segmented tabs ────────────────────────────────────────────────────────────
// Order: Directory | Roles & permissions  (Signups moved to Labs — VRG-133)
//
// The first tab was "Internal", which did no work: every page under `/internal/*` is
// internal, so the word named the console rather than the tab's contents. "Directory"
// and "Roles & permissions" now read as the actual split — WHO they are, and WHAT they
// can do.
//
// Three names were rejected, each for a different collision:
//   "Users"  — the page is named for humans precisely to stop colliding with company
//              users and certificate owners; a Users tab puts that back one level down.
//   "People" — the PAGE was called People (Team since 2026-10-01), and a tab repeating its
//              own page title is the stacked-duplicate-header shape §52 records from the
//              gift flow.
//
// The `value` stays 'internal' — it is a URL/query token, not a label, and renaming it
// would break any link carrying ?tab=internal for no visible gain.
const USER_TABS = [
  { label: 'Directory', value: 'internal' },
  { label: 'Roles & permissions', value: 'roles' },
]

type InternalUserRow = InternalUser & {
  companies?: Array<Pick<Company, 'id' | 'name' | 'logo'>>
}

export const InternalUsersPage = () => {
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION)
  const [isInviteModalOpen, { open: openInviteModal, close: closeInviteModal }] = useDisclosure(false)
  const [isEditModalOpen, { open: openEditModal, close: closeEditModal }] = useDisclosure(false)
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false)
  const [isArchiveModalOpen, { open: openArchiveModal, close: closeArchiveModal }] = useDisclosure(false)
  const [isAssignModalOpen, { open: openAssignModal, close: closeAssignModal }] = useDisclosure(false)
  const [selectedUser, setSelectedUser] = useState<InternalUserRow | null>(null)
  // Staff "sign out everywhere". platform_admin only (API-enforced); other internal roles
  // see the item disabled with the reason (§27: permission disables, never hides).
  const [signOutTarget, setSignOutTarget] = useState<InternalUserRow | null>(null)
  const { mutateAsync: revokeUserSessions, isPending: isRevokingUserSessions } = useRevokeUserSessions()
  const { data: viewerProfile } = useUserProfileQuery()
  const viewerIsPlatformAdmin = viewerProfile?.role?.name === 'platform_admin'
  const [showArchived, setShowArchived] = useState(false)
  const [activeTab, setActiveTab] = useState('internal')
  // Bulk selection + action state for the Internal tab (mirrors the company UsersPage).
  const [rowSelectionModel, setRowSelectionModel] = useState<any[]>([])
  const [bulkAction, setBulkAction] = useState<BulkUserAction | null>(null)
  const [bulkUsers, setBulkUsers] = useState<InternalUserRow[]>([])
  const [isBulkPending, setIsBulkPending] = useState(false)
  const { open: notification } = useSnackbar()

  const { data, isLoading, refetch } = useInternalUsers({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
  })

  const allRows: InternalUserRow[] = (data?.data ?? []) as InternalUserRow[]

  // Filter out archived users (deleted_at IS NOT NULL) unless showArchived is true
  const rows = useMemo(() => {
    if (showArchived) {
      return allRows
    }
    return allRows.filter(user => !user.deleted_at)
  }, [allRows, showArchived])

  const total = rows.length

  // Resend invitation mutation
  const { mutate: resendInvitation, isPending: isResending } = useInviteInternalUser({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Successfully resent invitation',
      })
      refetch()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error.message || 'Failed to resend invitation'
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  // Bulk lifecycle mutations — looped over the selection from the bulk-action modal.
  const { mutateAsync: updateInternalUserAsync } = useUpdateInternalUser()
  const { mutateAsync: deleteInternalUserAsync } = useDeleteInternalUser()

  const openBulkModal = (action: BulkUserAction, selectedRows: InternalUserRow[]) => {
    setBulkUsers(selectedRows)
    setBulkAction(action)
  }

  const handleConfirmBulkAction = async () => {
    if (!bulkAction || bulkUsers.length === 0) return
    setIsBulkPending(true)
    try {
      const results = await Promise.allSettled(
        bulkUsers.map((u) =>
          bulkAction === 'delete'
            ? deleteInternalUserAsync({ userId: u.id })
            : updateInternalUserAsync({ userId: u.id, active: bulkAction === 'reactivate' }),
        ),
      )
      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - succeeded
      const verb = bulkAction === 'delete' ? 'deleted' : bulkAction === 'reactivate' ? 'reactivated' : 'archived'
      if (succeeded > 0) notification({ type: 'success', title: `${succeeded} user${succeeded > 1 ? 's' : ''} ${verb}` })
      if (failed > 0) notification({ type: 'error', title: `${failed} user${failed > 1 ? 's' : ''} could not be ${verb}` })
      setRowSelectionModel([])
      setBulkAction(null)
      refetch()
    } finally {
      setIsBulkPending(false)
    }
  }

  // Bulk-action bar entries. When every selected row is already archived we offer
  // Reactivate instead of Archive, mirroring the row kebab + company UsersPage.
  const getInternalBulkActions = useCallback((selectedRows: InternalUserRow[]) => {
    const allArchived =
      selectedRows.length > 0 && selectedRows.every((r) => !!r.deleted_at || r.active === false)
    return [
      allArchived
        ? { label: 'Reactivate', onClick: () => openBulkModal('reactivate', selectedRows), icon: <RotateCcwIcon strokeWidth={1} /> }
        : { label: 'Archive', onClick: () => openBulkModal('archive', selectedRows), icon: <ArchiveIcon strokeWidth={1} /> },
      { label: 'Delete', onClick: () => openBulkModal('delete', selectedRows), icon: <Trash2Icon strokeWidth={1} />, destructive: true },
    ]
  }, [])

  const handleEditUser = (user: InternalUserRow) => {
    setSelectedUser(user)
    openEditModal()
  }

  const handleDeleteUser = (user: InternalUserRow) => {
    setSelectedUser(user)
    openDeleteModal()
  }

  const handleArchiveUser = (user: InternalUserRow) => {
    setSelectedUser(user)
    openArchiveModal()
  }

  const handleAssignCompanies = (user: InternalUserRow) => {
    setSelectedUser(user)
    openAssignModal()
  }

  const handleResendInvite = (user: InternalUserRow) => {
    if (!user?.email || !user?.role?.name) {
      notification({
        type: 'error',
        title: 'Cannot resend invitation: user email or role not found',
      })
      return
    }

    // Determine company IDs for platform managers
    const companyIds = user.role.name === 'platform_manager'
      ? user.companies?.map(c => c.id).filter(Boolean) as string[]
      : undefined

    resendInvitation({
      email: user.email,
      role_name: user.role.name as 'platform_admin' | 'platform_manager',
      company_ids: companyIds,
    })
  }

  const getActionItems = (row: InternalUserRow) => {
    // Determine status: invited (no workos_user_id) or active. Archived rows are filtered upstream.
    const status = !row.workos_user_id ? 'invited' : 'active'

    const items = []

    // Manager-only: Assign Companies (above Edit)
    if (row.role?.name === 'platform_manager') {
      items.push({
        label: 'Assign Companies',
        onClick: () => handleAssignCompanies(row),
        icon: <BuildingIcon strokeWidth={1} />
      })
    }

    items.push({
      label: 'Edit',
      onClick: () => handleEditUser(row),
      icon: <PencilIcon strokeWidth={1} />
    })

    // Resend Invitation (if invited) — before Archive
    if (status === 'invited') {
      items.push({
        label: 'Resend Invitation',
        onClick: () => handleResendInvite(row),
        icon: <MailIcon strokeWidth={1} />
      })
    }

    // Sign out everywhere — only for someone who can actually be signed in (active and has
    // completed sign-up). Not shown for an invite: there is no session to end (§46: record
    // state hides).
    if (row.active && row.workos_user_id) {
      items.push({
        label: 'Sign out everywhere',
        onClick: () => setSignOutTarget(row),
        icon: <LogOutIcon strokeWidth={1} />,
        disabled: !viewerIsPlatformAdmin,
        tooltip: viewerIsPlatformAdmin ? undefined : 'Only platform admins can sign other people out',
      })
    }

    // Archive (reversible deactivate) — only if active
    if (row.active) {
      items.push({
        label: 'Archive',
        onClick: () => handleArchiveUser(row),
        icon: <ArchiveIcon strokeWidth={1} />,
        divider: true
      })
    }

    // Delete (permanent hard delete) — always shown, last in list
    items.push({
      label: 'Delete',
      onClick: () => handleDeleteUser(row),
      icon: <Trash2Icon strokeWidth={1} />,
      destructive: true, // Delete is always red (matches the company-facing kebab)
      divider: !row.active, // divider before delete when archive is hidden
    })

    return items
  }

  const columns = useMemo(
    () => [
      {
        field: 'user',
        headerName: 'User',
        type: 'string',
        width: 250,
        sortable: false,
        disableColumnMenu: true,
        pinned: 'left',
        renderCell: ({ row }: { row: InternalUserRow }) => {
          const name = getNameOrEmailSlug(row)
          return (
            <Stack direction="row" spacing={2} alignItems="center" sx={cellFlexSx}>
              <Avatar src={row.avatar} name={name} size={40} />
              <Box
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" noWrap>
                  {name}
                </Typography>
                <Typography variant="body3" color="text.secondary" noWrap>
                  {row.email}
                </Typography>
              </Box>
            </Stack>
          )
        },
      },
      {
        field: 'companies',
        headerName: 'Companies',
        type: 'string',
        width: 220,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: { row: InternalUserRow }) => {
          const companies = row.companies ?? []
          const isAdmin = row.role?.name === 'platform_admin'

          // Show "All" for Admin role
          if (isAdmin) {
            return (
              <Box sx={cellFlexSx}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  All
                </Typography>
              </Box>
            )
          }

          // Show avatars for Manager and other roles
          if (!companies.length) {
            return (
              <Box sx={cellFlexSx}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  —
                </Typography>
              </Box>
            )
          }

          return <CompanyGroupCell companies={companies} />
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        type: 'string',
        width: 160,
        sortable: false,
        renderCell: ({ row }: { row: InternalUserRow }) => {
          const roleLabel = getMemberRoleDisplayName(row.role?.name)
          return (
            <Box sx={cellFlexSx}>
              <Typography variant="body3" noWrap>
                {roleLabel}
              </Typography>
            </Box>
          )
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        type: 'string',
        width: 150,
        sortable: false,
        renderCell: ({ row }) => {
          // Determine status: invited (no workos_user_id) or active. Archived rows are filtered upstream.
          const status = !row.workos_user_id ? 'invited' : 'active'
          return (
            <Box sx={cellFlexSx}>
              <StatusChip
                size="small"
                statuses={INTERNAL_USER_STATUSES}
                status={status}
              />
            </Box>
          )
        },
      },
      {
        field: 'created_at',
        headerName: 'Registration date',
        type: 'dateTime',
        width: 200,
        sortable: false,
        valueGetter: ({ value }) => (value ? new Date(value as string) : null),
        renderCell: ({ row }: { row: InternalUserRow }) => (
          <Box sx={cellFlexSx}>
            <Typography variant="body2" color="text.secondary">
              {row.created_at ? date(row.created_at) : '—'}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        disableReorder: true,
        pinned: 'right',
        renderCell: ({ row }: { row: InternalUserRow }) => (
          <ActionDropdown items={getActionItems(row)} />
        ),
      },
    ],
    []
  )

  const handlePaginationChange = (model: typeof paginationModel) => {
    setPaginationModel(model)
  }

  const renderEmptyState = () => (
    <EmptyState
      title="Directory unavailable"
      text="This internal tool is disabled until it can fetch live data."
      icon={<UsersIcon strokeWidth={1} size={32} />}
    />
  )

  return (
    <>
      {/*
        VRG-470: tabs in their OWN full-width row below the title, not `Header`'s
        `leftComponents` — that slot renders inside the title row, which `Header` pins to
        `minHeight: 49` (§29), and the underline row is 52. Measured on /internal/analytics
        before the same move: title centre 46.0 against the sidebar-logo datum of 44.0. An
        underline also needs a full-width band; inline it renders as a fragment beside the
        title. The page's own action stays in `rightComponents`, as on Billing.
      */}
      {/* "Team" since 2026-10-01 (was "People", which now names the access table at
          /internal/people). Matches the sidebar row and the command palette. */}
      <Header
        title="Team"
        total={activeTab === 'internal' ? (data?.total ?? 0) : undefined}
        rightComponents={
          activeTab === 'internal' ? (
            <Stack direction="row" spacing={3} alignItems="center">
              <Button
                variant="primary"
                disableElevation
                onClick={openInviteModal}
                startIcon={<UserPlusIcon strokeWidth={1} />}
              >
                Invite
              </Button>
            </Stack>
          ) : undefined
        }
      />
      <Tabs redirect={false} tabs={USER_TABS} value={activeTab} onChange={setActiveTab} sx={{ mb: `${TABS_CONTENT_GAP}px` }} />

      {activeTab === 'internal' ? (
        <Table
          loading={isLoading}
          columns={columns}
          rows={rows}
          alwaysShowRowSelection={false}          bulkActions={getInternalBulkActions}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSelectionModel}
          fillBottom
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          emptyState={renderEmptyState()}
        />
      ) : (
        // Title→content gap matches standard data-table pages (pure fillBottom, no
        // topOffset offset, no extra pt) so both tabs sit at the same distance.
        <PermissionsMatrix scope="internal" />
      )}

      <InviteInternalUserModal
        type="platform"
        open={isInviteModalOpen}
        onClose={closeInviteModal}
      />

      <BulkUserActionModal
        users={bulkUsers}
        action={bulkAction ?? 'archive'}
        open={bulkAction !== null}
        onClose={() => setBulkAction(null)}
        onConfirm={handleConfirmBulkAction}
        isLoading={isBulkPending}
      />

      <ConfirmSettingModal
        open={Boolean(signOutTarget)}
        onClose={() => setSignOutTarget(null)}
        onConfirm={async () => {
          if (!signOutTarget) return
          try {
            const { revoked } = await revokeUserSessions(signOutTarget.id)
            notification({
              type: 'success',
              title: 'Signed out everywhere',
              text: revoked === 1 ? 'Ended 1 session.' : `Ended ${revoked} sessions.`,
            })
            setSignOutTarget(null)
          } catch (error) {
            notification({ type: 'error', title: 'Could not sign them out', text: getErrorMessage(error) })
          }
        }}
        title="Sign this person out everywhere?"
        consequence={`Every device ${signOutTarget?.email ?? 'this person'} is signed in on will be signed out on its next request. They can sign in again straight away, with their second factor. Use Archive instead if they should not get back in.`}
        confirmLabel="Sign out everywhere"
        destructive
        loading={isRevokingUserSessions}
      />

      {selectedUser && (
        <>
          <EditInternalUserModal
            user={selectedUser}
            open={isEditModalOpen}
            onClose={closeEditModal}
          />
          <ArchiveInternalUserModal
            user={selectedUser}
            open={isArchiveModalOpen}
            onClose={closeArchiveModal}
            onArchived={refetch}
          />
          <DeleteInternalUserModal
            user={selectedUser}
            open={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDeleted={refetch}
          />
          <AssignCompaniesModal
            user={selectedUser}
            open={isAssignModalOpen}
            onClose={closeAssignModal}
            onUpdated={refetch}
          />
        </>
      )}
    </>
  )
}
